import {FileRecord} from '@packages/systems/core/records/FileRecord';
import type {AssetState} from '@packages/systems/designer/assets';
import type {IX2StoreStateType} from '@packages/systems/ix2/types-core';
import {importIx2FromElementPreset} from '@packages/systems/ix2/shared-utils-IX2Utils';
import {getLibrariesVariablesOriginIdMap} from '@packages/systems/libraries';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {
  importNodeAddedStylesFromElementPreset,
  reconstructAssetMap,
} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockPresetUtils';
import {generateGuid} from '@packages/utilities/guid';
import {weakMemo} from '@packages/utilities/memo';
import {
  getComponentByOrigin,
  replaceOriginComponentInstancesWithLocalComponents,
  updateXscpStyleBlockIds,
} from '@packages/utilities/platform';
import {getPluginsFromState} from '@packages/utilities/platform-selectors';
import type {
  DesignerActionFromCreator,
  DesignerStores,
} from '@packages/systems/designer/flux';
import {
  importIx3FromElementPreset,
  type IX3State,
} from '@packages/systems/ix3/store';
import {
  Component,
  ElementPreset,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {getPluginName, type Qualified} from '@packages/systems/wfdl-Qualified';
import {
  libraryComponentCreated,
  resolveLibraryAssetLocalCopy,
} from '@packages/systems/libraries-designer';

/**
 * Carries complete next state slices after preparing a library component and its
 * dependencies. Callers apply these slices directly or extract a staged paste delta.
 */
export type CreateLibraryComponentResult = {
  componentMap: Component.ComponentMap;
  styleBlockState: StyleBlockStateType;
  ix2State: IX2StoreStateType;
  ix3State: IX3State;
  // Only newly required assets; the other fields include existing site resources.
  assetsToImport: {[key: string]: FileRecord};
};

function unchangedResult({
  componentMap,
  styleBlockState,
  ix2State,
  ix3State,
}: {
  componentMap: Component.ComponentMap;
  styleBlockState: StyleBlockStateType;
  ix2State: IX2StoreStateType;
  ix3State: IX3State;
}): CreateLibraryComponentResult {
  return {
    componentMap,
    styleBlockState,
    ix2State,
    ix3State,
    assetsToImport: {},
  };
}

function importElementPresetAssets({
  assets,
  elementPreset,
  pluginName,
  plugins,
}: {
  assets: AssetState['assets'];
  elementPreset: NonNullable<ReturnType<typeof Plugins.getElementPreset>>;
  pluginName: string;
  plugins: Plugins.PluginsType;
}): {
  assets: AssetState['assets'];
  assetsToImport: {[key: string]: FileRecord};
} {
  const presetAssets =
    Plugins.getElementPresetAssets(plugins, elementPreset) ?? [];
  let nextAssets = assets;
  const assetsToImport: {[key: string]: FileRecord} = {};

  for (const presetAsset of presetAssets) {
    const assetRecord = FileRecord.reify({
      ...presetAsset,
      versionedResource: pluginName,
    });
    const {localCopy, assetsToImport: assetToImport} =
      resolveLibraryAssetLocalCopy({
        assets: nextAssets,
        asset: assetRecord,
      });
    if (assetToImport) {
      Object.assign(assetsToImport, assetToImport);
    }
    nextAssets = nextAssets.set(localCopy._id, localCopy);
  }

  return {assets: nextAssets, assetsToImport};
}

/**
 * Materializes a local site component (and its preset dependencies) from a library
 * element preset origin. Intent helper for `LIBRARY_COMPONENT_CREATED` — reducers
 * call this once per action (weakMemo'd) and apply their slice from the result.
 *
 * Currently imports: componentMap, styleBlockState, ix2State, ix3State,
 * assetsToImport. To add another resource on library component creation, extend
 * `CreateLibraryComponentResult` here and wire a `LIBRARY_COMPONENT_CREATED`
 * case in the owning store reducer (see StyleBlockStore, IX2UiStore,
 * ix3WrapperReducer, componentMapReducer, librariesAssetStoreReducer).
 *
 * No-op when a local copy already exists or import strategy is not VALUE; returns
 * unchanged slice references in that case.
 */
export const createLibraryComponent = weakMemo(
  (
    action: DesignerActionFromCreator<typeof libraryComponentCreated>
  ): CreateLibraryComponentResult => {
    const {state, payload} = action;
    return prepareLibraryComponent({
      origin: payload.origin,
      componentMap: state.DesignerStore.components,
      styleBlockState: state.StyleBlockStore,
      ix2State: state.IX2UiStore,
      ix3State: state.IX3Store,
      assets: state.AssetStore.assets,
      plugins: getPluginsFromState(state),
      cssVariables: state.CssVariablesStore,
      currentPageId: state.DesignerStore.currentPageId,
      libraryProfileId:
        state.DesignerStore.libraries?.[getPluginName(payload.origin)]
          ?.profileId,
    });
  }
);

// Preparation stays separate from dispatch so paste can validate before committing.
export function prepareLibraryComponent({
  origin,
  componentMap: components,
  styleBlockState,
  ix2State,
  ix3State,
  assets,
  plugins,
  cssVariables: {variables, variableCollections},
  currentPageId,
  libraryProfileId,
}: {
  origin: Qualified<Component.ComponentName>;
  componentMap: Component.ComponentMap;
  styleBlockState: StyleBlockStateType;
  ix2State: IX2StoreStateType;
  ix3State: IX3State;
  assets: AssetState['assets'];
  plugins: Plugins.PluginsType;
  cssVariables: Pick<
    DesignerStores['CssVariablesStore'],
    'variables' | 'variableCollections'
  >;
  currentPageId: DesignerStores['DesignerStore']['currentPageId'];
  libraryProfileId?: string;
}): CreateLibraryComponentResult {
  const existing = getComponentByOrigin(origin, components);
  if (existing.componentId) {
    return unchangedResult({
      componentMap: components,
      styleBlockState,
      ix2State,
      ix3State,
    });
  }

  const elementPreset = Plugins.getElementPreset(plugins, origin);
  if (!elementPreset) {
    return unchangedResult({
      componentMap: components,
      styleBlockState,
      ix2State,
      ix3State,
    });
  }

  const importStrategy = Plugins.getElementPresetImportStrategy(
    plugins,
    origin
  );
  if (importStrategy !== Plugins.IMPORT_STRATEGY.VALUE) {
    return unchangedResult({
      componentMap: components,
      styleBlockState,
      ix2State,
      ix3State,
    });
  }

  const element = Expressions.getElement(
    ElementPreset.instantiateFactory(elementPreset, generateGuid()).element
  );
  if (!element) {
    return unchangedResult({
      componentMap: components,
      styleBlockState,
      ix2State,
      ix3State,
    });
  }

  const pluginName = getPluginName(origin);
  const {assets: nextAssets, assetsToImport} = importElementPresetAssets({
    assets,
    elementPreset,
    pluginName,
    plugins,
  });

  const {idMap: styleBlocksIdMap, state: importedStyleBlockState} =
    importNodeAddedStylesFromElementPreset({
      elementPresetType: elementPreset,
      pluginName,
      initialStyleBlockId: generateGuid(),
      styleBlockState,
      assets: nextAssets,
      variables,
      variableCollections,
      elementPresetImportStrategy: importStrategy,
      plugins,
    });

  const assetIdMap = reconstructAssetMap(
    nextAssets,
    null,
    elementPreset,
    pluginName,
    importStrategy,
    plugins
  ).toObject();

  const variableIdMap = getLibrariesVariablesOriginIdMap(variables)(pluginName);

  const result = replaceOriginComponentInstancesWithLocalComponents({
    plugins,
    element,
    componentMap: components,
    idMap: assetIdMap,
    updateComponentRenderFn: ({render: expr}) =>
      updateXscpStyleBlockIds(styleBlocksIdMap)(expr),
  });

  const nextComponentMap = result.componentMap;
  const idMap = {
    ...result.idMap,
    ...styleBlocksIdMap,
    ...variableIdMap,
  };
  const nextIx2State = importIx2FromElementPreset(
    plugins,
    origin,
    ix2State,
    idMap,
    currentPageId
  );
  const nextIx3State = importIx3FromElementPreset(ix3State, {
    elementPreset,
    idMap,
    styleBlocksIdMap,
    currentPageId,
    libraryProfileId,
  });

  const local = getComponentByOrigin(origin, nextComponentMap);
  if (!local.componentId) {
    return unchangedResult({
      componentMap: components,
      styleBlockState,
      ix2State,
      ix3State,
    });
  }

  if (nextComponentMap === components) {
    return unchangedResult({
      componentMap: components,
      styleBlockState,
      ix2State,
      ix3State,
    });
  }

  return {
    componentMap: nextComponentMap,
    styleBlockState: importedStyleBlockState,
    ix2State: nextIx2State,
    ix3State: nextIx3State,
    assetsToImport,
  };
}
